import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";

import {
  getExternalRegisterAlertTypeLabel,
} from "@/components/externalregister/helpers";

import {
  CreateEntryDialog,
  DeleteNeverLinkedEntryDialog,
  EditEntryDialog,
  EntryActionDialog,
  EntryDetailsDialog,
} from "./ExternalRegisterEntryDialogs";
import { PatientCell } from "./ExternalRegisterPatientCell";
import { useExternalRegisterAdmin } from "./externalRegisterAdminProvider";
import { getAlertTypeChipColor, isNeverLinkedEntry } from "./externalRegisterAdminHelpers";
import type {
  ExternalRegisterAdminActionDialogState,
  ExternalRegisterAlertTypeFilter,
  ExternalRegisterLinkStatusFilter,
  ExternalRegisterMaintenanceEntry,
  ExternalRegisterStatusFilter,
} from "./externalRegisterAdminTypes";
import { EXTERNAL_REGISTER_ADMIN_ALERT_TYPES } from "./externalRegisterAdminTypes";
import {
  compactAdminTableSx,
  compactOutlinedButtonSx,
  compactSelectSx,
  panelSx,
} from "./externalRegisterAdminUi";

const MaintenanceFilters = () => {
  const { t } = useTranslation();
  const { filters, setFilters, refreshEntries, loadingEntries } = useExternalRegisterAdmin();

  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} alignItems={{ lg: "center" }}>
        <FormControl size="small" sx={{ ...compactSelectSx, minWidth: 150 }}>
          <InputLabel id="external-register-status-filter-label">{t("common.filters.status")}</InputLabel>
          <Select
            labelId="external-register-status-filter-label"
            label={t("common.filters.status")}
            value={filters.status}
            onChange={(event) => setFilters({ ...filters, status: event.target.value as ExternalRegisterStatusFilter })}
          >
            <MenuItem value="active">{t("common.status.active")}</MenuItem>
            <MenuItem value="inactive">{t("common.status.inactive")}</MenuItem>
            <MenuItem value="all">{t("externalRegister.admin.maintenance.filters.all")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ ...compactSelectSx, minWidth: 210 }}>
          <InputLabel id="external-register-type-filter-label">{t("externalRegister.admin.maintenance.filters.alertType")}</InputLabel>
          <Select
            labelId="external-register-type-filter-label"
            label={t("externalRegister.admin.maintenance.filters.alertType")}
            value={filters.type}
            onChange={(event) => setFilters({ ...filters, type: event.target.value as ExternalRegisterAlertTypeFilter })}
          >
            <MenuItem value="all">{t("externalRegister.admin.maintenance.filters.allTypes")}</MenuItem>
            {EXTERNAL_REGISTER_ADMIN_ALERT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {getExternalRegisterAlertTypeLabel(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={t("externalRegister.admin.maintenance.filters.patientId")}
          value={filters.patientId}
          onChange={(event) => setFilters({ ...filters, patientId: event.target.value })}
          autoComplete="new-password"
          inputProps={{
            "aria-label": t("externalRegister.admin.maintenance.filters.patientIdAriaLabel"),
            autoComplete: "new-password",
            name: "external-register-filter-patient-id",
          }}
          sx={{
            minWidth: 210,
            "& .MuiInputBase-root": {
              minHeight: 40,
            },
          }}
        />
        <FormControl size="small" sx={{ ...compactSelectSx, minWidth: 160 }}>
          <InputLabel id="external-register-link-filter-label">{t("externalRegister.admin.maintenance.filters.linkStatus")}</InputLabel>
          <Select
            labelId="external-register-link-filter-label"
            label={t("externalRegister.admin.maintenance.filters.linkStatus")}
            value={filters.linkStatus}
            onChange={(event) => setFilters({ ...filters, linkStatus: event.target.value as ExternalRegisterLinkStatusFilter })}
          >
            <MenuItem value="linked">{t("externalRegister.admin.maintenance.filters.linked")}</MenuItem>
            <MenuItem value="unlinked">{t("externalRegister.admin.maintenance.filters.unlinked")}</MenuItem>
            <MenuItem value="all">{t("externalRegister.admin.maintenance.filters.all")}</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" size="small" onClick={refreshEntries} disabled={loadingEntries}>
          {t("externalRegister.admin.buttons.reloadEntries")}
        </Button>
      </Stack>
    </Paper>
  );
};

const MaintenanceTable = ({
  onCreate,
  onEdit,
  onDetails,
  onAction,
  onDeleteNeverLinked,
}: {
  onCreate: () => void;
  onEdit: (entry: ExternalRegisterMaintenanceEntry) => void;
  onDetails: (entry: ExternalRegisterMaintenanceEntry) => void;
  onAction: (state: ExternalRegisterAdminActionDialogState) => void;
  onDeleteNeverLinked: (entry: ExternalRegisterMaintenanceEntry) => void;
}) => {
  const { t } = useTranslation();
  const {
    entries,
    total,
    loadingEntries,
    sampleDataActive,
    settings,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
  } = useExternalRegisterAdmin();
  const hasNeverLinkedRows = entries.some(isNeverLinkedEntry);

  const renderActionButton = (
    label: string,
    disabled: boolean,
    onClick: () => void,
  ) => (
    <Button
      variant="outlined"
      size="small"
      sx={compactOutlinedButtonSx}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </Button>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        ...panelSx,
        height: "100%",
        minHeight: 0,
        display: "flex",
      }}
    >
      <CardContent sx={{ display: "flex", flex: 1, minHeight: 0, p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, width: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {t("externalRegister.admin.maintenance.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("externalRegister.admin.maintenance.showingCount", { showing: entries.length, total })}
                {sampleDataActive ? t("externalRegister.admin.maintenance.sampleDataSuffix") : ""}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {sampleDataActive ? <Chip size="small" variant="outlined" label={t("externalRegister.admin.maintenance.sampleDataChip")} /> : null}
              <Button variant="contained" size="small" onClick={onCreate}>
                {t("externalRegister.admin.buttons.createManualEntry")}
              </Button>
            </Stack>
          </Stack>
          <MaintenanceFilters />
          <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table stickyHeader size="small" sx={compactAdminTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "26%" }}>{t("externalRegister.admin.maintenance.table.headers.patient")}</TableCell>
                  <TableCell sx={{ width: 175 }}>{t("externalRegister.admin.maintenance.table.headers.alertType")}</TableCell>
                  <TableCell sx={{ width: "15%" }}>{t("externalRegister.admin.maintenance.table.headers.hospital")}</TableCell>
                  <TableCell sx={{ width: 120 }}>{t("externalRegister.admin.maintenance.table.headers.dateListed")}</TableCell>
                  <TableCell sx={{ width: 110 }}>{t("externalRegister.admin.maintenance.table.headers.status")}</TableCell>
                  <TableCell sx={{ width: 118 }}>{t("externalRegister.admin.maintenance.table.headers.linkStatus")}</TableCell>
                  <TableCell align="right" sx={{ width: 260 }}>{t("externalRegister.admin.maintenance.table.headers.actions")}</TableCell>
                  {hasNeverLinkedRows ? <TableCell aria-label={t("externalRegister.admin.maintenance.table.headers.neverLinkedRemoval")} sx={{ width: 44, p: 0 }} /> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7 + (hasNeverLinkedRows ? 1 : 0)}>
                      <Typography variant="body2" color="text.secondary">
                        {loadingEntries ? t("externalRegister.admin.maintenance.table.loading") : t("externalRegister.admin.maintenance.table.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : entries.map((entry, rowIndex) => (
                  <TableRow
                    key={entry.id}
                    hover
                    sx={{
                      backgroundColor: rowIndex % 2 === 0 ? "background.paper" : "grey.50",
                    }}
                  >
                    <TableCell>
                      <PatientCell entry={entry} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getExternalRegisterAlertTypeLabel(entry.type)}
                        sx={(theme) => {
                          const backgroundColor = getAlertTypeChipColor(entry.type, settings);
                          return {
                            minWidth: 0,
                            backgroundColor,
                            color: theme.palette.getContrastText(backgroundColor),
                            fontWeight: 700,
                          };
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{entry.hospital}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{entry.dateListed}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={entry.status === "active" ? "success" : "default"}
                        label={entry.status === "active" ? t("common.status.active") : t("common.status.inactive")}
                        sx={{ minWidth: 72 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={isNeverLinkedEntry(entry)
                          ? t("externalRegister.admin.maintenance.table.neverLinked")
                          : entry.linkStatus === "linked"
                            ? t("externalRegister.admin.maintenance.filters.linked")
                            : t("externalRegister.admin.maintenance.filters.unlinked")}
                        sx={{ minWidth: 76 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                          gap: 0.5,
                          width: 230,
                          ml: "auto",
                        }}
                      >
                        {renderActionButton(t("externalRegister.admin.maintenance.actions.history"), false, () => onDetails(entry))}
                        {renderActionButton(t("common.buttons.edit"), false, () => onEdit(entry))}
                        {entry.status === "active"
                          ? renderActionButton(t("externalRegister.admin.maintenance.actions.inactivate"), false, () => onAction({ kind: "inactivate", entry }))
                          : renderActionButton(t("externalRegister.admin.maintenance.actions.reactivate"), false, () => onAction({ kind: "reactivate", entry }))}
                        {renderActionButton(t("externalRegister.admin.maintenance.actions.unlink"), entry.linkStatus !== "linked", () => onAction({ kind: "unlink", entry }))}
                        {renderActionButton(t("externalRegister.admin.maintenance.actions.relink"), false, () => onAction({ kind: "relink", entry }))}
                      </Box>
                    </TableCell>
                    {hasNeverLinkedRows ? (
                      <TableCell align="center" sx={{ width: 44, px: 0.25 }}>
                        {isNeverLinkedEntry(entry) ? (
                          <Tooltip title={t("externalRegister.admin.maintenance.actions.deleteNeverLinked")}>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={t("externalRegister.admin.maintenance.actions.deleteNeverLinkedAriaLabel", { id: entry.id })}
                              onClick={() => onDeleteNeverLinked(entry)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
            }}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              minHeight: 42,
              overflow: "hidden",
              "& .MuiToolbar-root": {
                minHeight: 42,
                pr: 0,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                my: 0,
                fontSize: "0.75rem",
              },
              "& .MuiTablePagination-select": {
                pr: "32px !important",
              },
              "& .MuiTablePagination-input": {
                mr: 1,
              },
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export const ExternalRegisterMaintenancePanel = () => {
  const [createOpen, setCreateOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<ExternalRegisterMaintenanceEntry | null>(null);
  const [detailsEntry, setDetailsEntry] = useState<ExternalRegisterMaintenanceEntry | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<ExternalRegisterMaintenanceEntry | null>(null);
  const [actionState, setActionState] = useState<ExternalRegisterAdminActionDialogState>(null);

  return (
    <Box sx={{ height: "100%", minHeight: 0 }}>
      <MaintenanceTable
        onCreate={() => setCreateOpen(true)}
        onEdit={setEditEntry}
        onDetails={setDetailsEntry}
        onAction={setActionState}
        onDeleteNeverLinked={setDeleteEntry}
      />
      <CreateEntryDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditEntryDialog entry={editEntry} onClose={() => setEditEntry(null)} />
      <EntryDetailsDialog entry={detailsEntry} onClose={() => setDetailsEntry(null)} />
      <DeleteNeverLinkedEntryDialog entry={deleteEntry} onClose={() => setDeleteEntry(null)} />
      <EntryActionDialog state={actionState} onClose={() => setActionState(null)} />
    </Box>
  );
};
